import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { IconButton, Grid, Button, TextField, MenuItem, Box, Typography, CircularProgress } from '@mui/material';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { ShopContext } from 'Context/ShopContext';
import { BARCODE_SETTINGS, STORAGE_KEYS, BARCODE_PRINTER_FORMATS_CUSTOM_PRN } from 'const';
import { AlertMessagesContext } from 'react-alert-messages';
import BarcodePrintService from 'services/BarcodePrinterService';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DateTimeHelpers from 'helpers/DateTimeHelpers';

function BarcodePrintDialog({ handleClose, items = [] }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [quantity, setQuantity] = useState(null);
  const [shelfDays, setShelfDays] = useState(null);
  const [packedDate, setPackedDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [batch, setBatch] = useState(null);
  const [customnote1, setCustomnote1] = useState(null);
  const [customnote2, setCustomnote2] = useState(null);
  const [count, setCount] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [isLoading, setIsloading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  useEffect(() => {
    const templates = shop?.config?.barcode_settings?.barcode_custom_prn_templates || [];
    const activeId = shop?.config?.barcode_settings?.active_template_id;
    if (templates.length > 0 && !selectedTemplateId) {
      const initialId = templates.find((t) => t.id === activeId) ? activeId : templates[0].id;
      setSelectedTemplateId(initialId);
    }
  }, [shop, selectedTemplateId]);

  useEffect(() => {
    setPackedDate(new Date());
  }, []);

  const handleCountChange = async (e) => {
    const value = e.target.value;
    setCount(value);
  };
  const handleQuantityChange = async (e) => {
    const value = e.target.value;
    setQuantity(value);
  };
  const handleShelfDaysChange = (e) => {
    const days = parseInt(e.target.value) || 0;
    setShelfDays(days);

    const packed = packedDate ? new Date(packedDate) : new Date();

    const expiry = new Date(packed);
    expiry.setDate(packed.getDate() + days);

    setExpiryDate(expiry);
  };
  const handlePackedDateChange = (date) => {
    setPackedDate(date);

    if (shelfDays) {
      const days = parseInt(shelfDays) || 0;
      const packed = date ? new Date(date) : new Date();
      const expiry = new Date(packed);
      expiry.setDate(packed.getDate() + days);
      setExpiryDate(expiry);
    }
  };
  const handleExpiryDateChange = (date) => {
    setExpiryDate(date);
  };
  const handleBatchChange = async (e) => {
    const value = e.target.value;
    setBatch(value);
  };
  const handleCustomnote1Change = async (e) => {
    const value = e.target.value;
    setCustomnote1(value);
  };
  const handleCustomnote2Change = async (e) => {
    const value = e.target.value;
    setCustomnote2(value);
  };
  const getFormattedPluQuantity = (plu, qtyKg) => {
    if (plu == null || qtyKg == null) return '';

    const pluPart = String(plu).padStart(4, '0');
    const qtyGrams = Math.floor(qtyKg * 1000);
    const qtyPart = String(qtyGrams).padStart(5, '0');

    return `$${pluPart}${qtyPart}$`;
  };

  const generatePrnData = (setting) => {
    let updatedItems = items.map((item) => ({
      ...item,
      quantity: item.quantity ?? count,
    }));

    let output = '';

    updatedItems.forEach((item) => {
      const qty = parseInt(item.quantity) || 1;
      const rows = qty % setting.column === 0 ? qty : qty + 1;
      const qtyQr = qty != null && item.unit === 'Kg' ? getFormattedPluQuantity(item.plu, Number(qty)) : '';

      const unitLabel = item.unit === 'Kg' ? 'g' : item.unit;
      const uspPrice = item.price / (1000 * (item.kg_ratio || 0)) + ' /' + unitLabel;
      const uspMrp = item.mrp / (1000 * item.kgRatio) + ' /' + unitLabel;
      const totalMrp = item.mrp * qty;

      for (let i = 0; i < rows; i++) {
        let code = setting.code
          .replace(/<title>/g, shop.shop_name || '')
          .replace(/<name>/g, item.name || '')
          .replace(/<barcode>/g, item.ean && item.ean !== 'nil' ? item.ean : `plu${item.plu}`)
          .replace(/<qty-qr>/, qtyQr)
          .replace(/<price>/g, item.price ? String(item.price) : '')
          .replace(/<mrp>/g, item.mrp ? String(item.mrp) : '')
          .replace(/<usp>/g, uspPrice || '')
          .replace(/<usp-mrp>/g, uspMrp || '')
          .replace(/<plu>/g, item.plu ? String(item.plu) : '')
          .replace(/<quantity>/g, String(quantity || qty))
          .replace(/<total>/g, item.total ? String(item.total) : '')
          .replace(/<total-mrp>/g, totalMrp || '')
          .replace(/<batch>/g, batch || '')
          .replace(/<packed>/g, DateTimeHelpers.convertDateToIsoDMY(packedDate) || '')
          .replace(/<expiry>/g, DateTimeHelpers.convertDateToIsoDMY(expiryDate) || '')
          .replace(/<custom-note>/g, item.note || '')
          .replace(/<custom-note-1>/g, customnote1 || '')
          .replace(/<custom-note-2>/g, customnote2 || '');

        output += code + '\n';
      }
    });

    return output;
  };

  const downloadPrn = (data) => {
    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'barcodes.prn';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadBarcode = () => {
    const format = shop?.config?.barcode_settings?.barcode_printer_format;

    if (!format) {
      postAlertMessage({
        text: tPopup('Barcode format not configured. Please set barcode format in shop setting.'),
        type: 'failed',
      });
      return;
    }

    const activeTemplate = (shop?.config?.barcode_settings?.barcode_custom_prn_templates || []).find((t) => t.id === selectedTemplateId);
    const setting =
      format === BARCODE_PRINTER_FORMATS_CUSTOM_PRN
        ? { code: activeTemplate ? activeTemplate.code : shop?.config?.barcode_settings?.barcode_custom_prn, column: 2 }
        : BARCODE_SETTINGS[format];

    const prnData = generatePrnData(setting);

    downloadPrn(prnData);
    handleClose();
  };

  const handlePrintBarcode = async () => {
    const format = shop?.config?.barcode_settings?.barcode_printer_format;

    if (!format) {
      postAlertMessage({
        text: tPopup('Barcode format not configured. Please set barcode format in shop setting.'),
        type: 'failed',
      });
      return;
    }

    if (!selectedPrinter) {
      postAlertMessage({
        text: tPopup('Please select a printer.'),
        type: 'failed',
      });
      return;
    }
    if (selectedPrinter.status !== 'None') {
      postAlertMessage({
        text: tPopup('Selected printer is in offline. Please select another printer.'),
        type: 'failed',
      });
      return; 
    }

    const activeTemplate = (shop?.config?.barcode_settings?.barcode_custom_prn_templates || []).find((t) => t.id === selectedTemplateId);
    const setting =
      format === BARCODE_PRINTER_FORMATS_CUSTOM_PRN
        ? { code: activeTemplate ? activeTemplate.code : shop?.config?.barcode_settings?.barcode_custom_prn, column: 1 }
        : BARCODE_SETTINGS[format];
    const prnData = generatePrnData(setting);

    try {
      await BarcodePrintService.printBarcode(selectedPrinter?.id, prnData);
    } catch (error) {
      postAlertMessage({
        text: tPopup('Failed to send print job. Please ensure the printing service is running.'),
        type: 'failed',
      });
    }
    handleClose();
  };

  const isDisableBtn = isLoading || (items.length === 1 && !items[0]?.quantity && (!count || parseInt(count) <= 0));

  const handleSelectPrinter = (printer) => {
    setSelectedPrinter(printer);
    localStorage.setItem(STORAGE_KEYS.SELECTED_BARCODE_PRINTER, printer?.id);
  };

  const fetchPrinters = useCallback(async () => {
    setIsloading(true);
    try {
      const storedPrinter = localStorage.getItem(STORAGE_KEYS.SELECTED_BARCODE_PRINTER);
      const data = await BarcodePrintService.getPrinters();

      if (Array.isArray(data) && data.length > 0) {
        setPrinters(data);

        let selected = null;

        if (storedPrinter) {
          const matched = data.find((p) => p.id === storedPrinter);
          if (matched) {
            if (matched.status === 'None') {
              selected = matched;
            }
          }
        }

        if (!selected) {
          selected = data.find((p) => p.status === 'None') || null;
        }

        handleSelectPrinter(selected);
      } else {
        setPrinters([]);
        setSelectedPrinter(null);
      }
    } catch (error) {
      setPrinters([]);
      setSelectedPrinter(null);
    }
    setIsloading(false);
  }, []);

  useEffect(() => {
    fetchPrinters();
  }, [fetchPrinters]);

  return (
    <>
      <div className={styles.backdrop}>
        <Grid container className={styles.modal}>
          <Grid item xs={12} className={styles.header}>
            <h2 className={styles.headerTitle}>{tCommon('Print Barcodes')}</h2>
            <IconButton onClick={handleClose} className={styles.closeBtn}>
              <ClearRoundedIcon onClick={handleClose} />
            </IconButton>
          </Grid>
          <Grid item xs={12} className={styles.inputWrapper}>
            <Grid display="flex" alignItems="center" justifyContent="space-between">
              <label htmlFor="count" className={styles.label}>
                {tCommon('Select Printer')}
              </label>
              <SyncRoundedIcon onClick={fetchPrinters} style={{ cursor: 'pointer', marginBottom: '2px' }} />
            </Grid>
            <TextField
              select
              name="creditOrder"
              variant="outlined"
              color="primary"
              className={styles.input}
              size="small"
              fullWidth
              onChange={(event) => {
                handleSelectPrinter(event.target.value);
              }}
              value={selectedPrinter}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: { maxHeight: 300 },
                  },
                },
              }}
              InputProps={{
                endAdornment: isLoading ? <CircularProgress size={25} style={{ marginRight: 8 }} /> : null,
              }}
            >
              {(!printers || printers.length === 0) && !isLoading && (
                <MenuItem disabled>
                  <em style={{ padding: '8px !important' }}>{tCommon('No Printers To List')}</em>
                </MenuItem>
              )}
              {printers &&
                printers.map((printer) => (
                  <MenuItem key={printer?.id} value={printer}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color={printer?.status === 'None' ? 'green' : 'red'}>
                        ●
                      </Typography>
                      <Typography variant="body2">{printer?.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          <Grid container spacing={1} item xs={12} className={styles.inputWrapper}>
            {shop?.config?.barcode_settings?.barcode_printer_format === BARCODE_PRINTER_FORMATS_CUSTOM_PRN &&
              (shop?.config?.barcode_settings?.barcode_custom_prn_templates || []).length > 0 && (
                <Grid item xs={12}>
                  <div className={styles.inputContainer}>
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      fullWidth
                      label={tPopup('Custom PRN Template')}
                      value={selectedTemplateId}
                      onChange={(e) => setSelectedTemplateId(e.target.value)}
                      className={styles.input}
                    >
                      {(shop?.config?.barcode_settings?.barcode_custom_prn_templates || []).map((tpl) => (
                        <MenuItem key={tpl.id} value={tpl.id}>
                          {tpl.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                </Grid>
              )}
            <Grid item xs={6} sm={6}>
              <div className={styles.inputContainer}>
                <TextField
                  type="text"
                  id="quantity"
                  value={quantity}
                  onChange={handleQuantityChange}
                  label={tCommon('Quantity')}
                  placeholder={tCommon('Quantity')}
                  className={styles.input}
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6}>
              <div className={styles.inputContainer}>
                <TextField
                  type="text"
                  id="shelfDays"
                  value={shelfDays}
                  onChange={handleShelfDaysChange}
                  label={tCommon('Shelf Days')}
                  placeholder={tCommon('Shelf Days')}
                  className={styles.input}
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={tCommon('Packed Date')}
                    inputFormat="dd/MM/yyyy"
                    value={packedDate}
                    onChange={handlePackedDateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        margin="normal"
                        id="packedDate"
                        className={styles.input}
                      />
                    )}
                  />
                </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={tCommon('Expiry Date')}
                    inputFormat="dd/MM/yyyy"
                    value={expiryDate}
                    onChange={handleExpiryDateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        margin="normal"
                        id="expiryDate"
                        className={styles.input}
                      />
                    )}
                  />
                </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm={6}>
              <div className={styles.inputContainer}>
                <TextField
                  label={tCommon('Batch')}
                  type="text"
                  id="batch"
                  value={batch}
                  onChange={handleBatchChange}
                  placeholder={tCommon('Batch')}
                  className={styles.input}
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6}>
              <div className={styles.inputContainer}>
                <TextField
                  type="text"
                  id="customnote1"
                  value={customnote1}
                  onChange={handleCustomnote1Change}
                  label={tCommon('Custom Note 1')}
                  placeholder={tCommon('Custom Note 1')}
                  className={styles.input}
                />
              </div>
            </Grid>
            <Grid item xs={6} sm={6}>
              <div className={styles.inputContainer}>
                <TextField
                  type="text"
                  id="customnote2"
                  value={customnote2}
                  onChange={handleCustomnote2Change}
                  label={tCommon('Custom Note 2')}
                  placeholder={tCommon('Custom Note 2')}
                  className={styles.input}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              {items.length === 1 && !items[0]?.quantity && (
                <>
                  <div className={styles.inputContainer}>
                    <TextField
                      type="text"
                      id="count"
                      value={count}
                      onChange={handleCountChange}
                      label={tCommon('Number of copies')}
                      placeholder={tCommon('Number of copies')}
                      className={styles.input}
                    />
                  </div>
                </>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} className={styles.footer}>
            <Button variant="contained" color="primary" className={styles.btnClose} size="small" onClick={handleClose}>
              {tCommon('Cancel')}
            </Button>
            <Button
              className={styles.btnSuccess}
              variant="contained"
              color="primary"
              size="small"
              disabled={isDisableBtn}
              onClick={() => {
                handleDownloadBarcode();
              }}
            >
              <FileDownloadRoundedIcon className={styles.btnIcon} /> .prn
            </Button>
            <Button
              className={styles.btnSuccess}
              variant="contained"
              color="primary"
              size="small"
              disabled={isDisableBtn || !selectedPrinter}
              onClick={() => {
                handlePrintBarcode();
              }}
            >
              <LocalPrintshopRoundedIcon className={styles.btnIcon} /> {tCommon('Print')}
            </Button>
          </Grid>
        </Grid>
      </div>
    </>
  );
}

export default BarcodePrintDialog;
